
import { useState, useEffect } from "react";
import { ExerciseDroppableContainer } from "./components/ExerciseDroppableContainer";
import { useDragDropHandler } from "./hooks/useDragDropHandler";
import { useExerciseOperations } from "./hooks/useExerciseOperations";
import { useUnifiedExerciseQuery } from "@/hooks/useUnifiedExerciseQuery";
import { useCacheCoordinator } from "@/hooks/useCacheCoordinator";

interface ExerciseListSectionProps {
  exercises: any[];
  routineId: string;
  onExerciseChange: () => void;
  showControls?: boolean;
  isPersonalPlan?: boolean;
}

export const ExerciseListSection = ({
  exercises: propExercises,
  routineId,
  onExerciseChange,
  showControls = true,
  isPersonalPlan = false
}: ExerciseListSectionProps) => {
  const [localExercises, setLocalExercises] = useState(propExercises || []);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const { isOperationLocked, isDragReady, cleanup } = useCacheCoordinator({ routineId });
  
  // DEBUG: Logs críticos para drag and drop
  console.log("🔍 DEBUG ExerciseListSection - Drag and Drop Status:", {
    routineId,
    isPersonalPlan,
    showControls,
    exerciseCount: localExercises.length,
    isDragReady: isDragReady(),
    isOperationLocked: isOperationLocked(),
    shouldEnableDrag: showControls && localExercises.length > 1
  });
  
  // Query unificada con configuración optimizada para estrategia zero-refetch
  const { data: unifiedExercises, refetch: refetchExercises } = useUnifiedExerciseQuery({ 
    routineId,
    enabled: true,
    staleTime: isPersonalPlan ? 30000 : 60000 // Tiempo reducido para planes personales
  });

  // Actualizaciones ultra-responsivas con merge inteligente y detección de cambios inmediatos
  useEffect(() => {
    const exercisesToUse = unifiedExercises || propExercises || [];
    
    // Lógica de actualización inteligente - solo actualizar si los datos realmente cambiaron
    const currentDataString = JSON.stringify(localExercises.map(ex => ({ id: ex.id, name: ex.name, position: ex.position })));
    const newDataString = JSON.stringify(exercisesToUse.map(ex => ({ id: ex.id, name: ex.name, position: ex.position })));
    
    if (newDataString !== currentDataString) {
      console.log("📋 Immediate exercise update detected:", {
        unifiedCount: unifiedExercises?.length || 0,
        propCount: propExercises?.length || 0,
        finalCount: exercisesToUse.length,
        routineId,
        isPersonalPlan,
        changeDetected: true
      });
      
      setLocalExercises(exercisesToUse);
      setLastUpdateTime(Date.now());
    }
  }, [unifiedExercises, propExercises, routineId, isPersonalPlan]);

  // Forzar refetch cuando se detecten cambios externos
  useEffect(() => {
    const handleExerciseChange = () => {
      console.log("🔄 Forcing exercise refetch due to external change");
      refetchExercises();
    };

    // Escuchar cambios desde el parent component
    if (onExerciseChange) {
      // Usar un pequeño delay para permitir que la DB se actualice
      const timeoutId = setTimeout(() => {
        handleExerciseChange();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [lastUpdateTime, refetchExercises, onExerciseChange]);

  const {
    handleDragStart,
    handleDragEnd,
    updateCollapsedState,
    getCollapsedState,
    canReorder,
    hasPendingOperations,
    pendingOperationsCount,
    isDragging,
    handleExerciseListChange,
    waitForPendingOperations,
    cancelPendingOperations
  } = useDragDropHandler({
    exercises: localExercises,
    routineId,
    onExerciseChange,
    showControls,
    onLocalUpdate: setLocalExercises
  });

  const {
    selectedExercise,
    editExerciseName,
    isEditDialogOpen,
    setEditExerciseName,
    setIsEditDialogOpen,
    handleUpdateExercise,
    handleDeleteExercise,
    handleEditClick
  } = useExerciseOperations({
    routineId,
    localExercises,
    setLocalExercises,
    onExerciseChange,
    updateExerciseField: () => {}
  });

  // DEBUG: Estado final antes de renderizar
  console.log("🎯 DEBUG ExerciseListSection - Final Render State:", {
    totalExercises: localExercises.length,
    isDragging,
    hasPendingOperations,
    pendingCount: pendingOperationsCount,
    isLocked: isOperationLocked(),
    isDragReady: isDragReady(),
    canReorder: canReorder(),
    isPersonalPlan,
    routineId,
    exerciseNames: localExercises.map(ex => ex.name)
  });

  // Cleanup ultra-optimizado - solo cuando es absolutamente necesario
  useEffect(() => {
    if (localExercises?.length > 0) {
      // Cleanup con debounce para evitar interferencias
      const cleanupTimer = setTimeout(() => {
        handleExerciseListChange();
      }, isPersonalPlan ? 3000 : 2000); // Tiempo reducido para respuesta más rápida
      
      return () => clearTimeout(cleanupTimer);
    }
  }, [localExercises, handleExerciseListChange, isPersonalPlan]);

  // Cleanup al desmontar con manejo de operaciones pendientes
  useEffect(() => {
    return () => {
      // Esperar operaciones pendientes antes de limpiar
      if (hasPendingOperations) {
        waitForPendingOperations().finally(() => {
          cleanup();
        });
      } else {
        cleanup();
      }
    };
  }, [cleanup, hasPendingOperations, waitForPendingOperations]);

  return (
    <ExerciseDroppableContainer
      exercises={localExercises}
      isPersonalPlan={isPersonalPlan}
      showControls={showControls}
      canReorder={canReorder()}
      getCollapsedState={getCollapsedState}
      updateCollapsedState={updateCollapsedState}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDeleteExercise={handleDeleteExercise}
      onUpdateExercise={handleUpdateExercise}
      onEditClick={handleEditClick}
      isEditDialogOpen={isEditDialogOpen}
      editExerciseName={editExerciseName}
      onEditNameChange={setEditExerciseName}
      onEditDialogOpenChange={setIsEditDialogOpen}
      routineId={routineId}
    />
  );
};
